import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Grid, makeStyles, Typography, debounce } from "@material-ui/core";
import { connect } from "react-redux";
import { Droppable } from "react-beautiful-dnd";

import { setShowingRouteId } from "../../store/actions/project";
import { routeColors } from "../data/constants";
import SearchBoxMessage from "../common/SearchBoxMessage";
import { FormattedText } from "../common/FormattedText";
import useStopsFilter from "../../hooks/useStopsFilter";
import Stop from "../project/Stop";
import { setLoadingRoutesList } from "../../store/actions/loading";
import { handleStopAdd } from "../../store/actions/custom";
import { dragFromUnassignedToRoute } from "../../store/actions/dragAndDrop";

import UnscheduledBar from "./UnscheduledBar";
import RouteHeader from "./RouteHeader";
import DragList from "./DragList";
import RouteBottomActions from "./RouteBottomActions";
import DragItem from "./DragItem";
import {
  setDragSelection,
  setDragSelectionByArr,
  setManualDragSelection,
  setProjectTabValue
} from "../../store/actions/common";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import { setExtendableId, setExtendableType } from "../../store/actions/edit";
import { store } from "../../index";
import useListKeyboardCommands from "../project/useListKeyboardCommands";

const useStyles = makeStyles(theme => ({
  title: {
    color: "#4D4D50",
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    margin: theme.spacing(1.5, 3)
  },
  searchBoxMessage: {
    paddingTop: theme.spacing(3)
  },
  suggestionTitle: {
    marginTop: theme.spacing(2)
  }
}));

const getSuggestionDebounce = debounce(
  (
    googleMapsAutocompleteService,
    googleMapsAutoCompleteServiceSession,
    mapBounds,
    searchValue,
    callback
  ) => {
    googleMapsAutocompleteService.getPlacePredictions(
      {
        input: searchValue,
        sessionToken: googleMapsAutoCompleteServiceSession,
        // Suggest addresses from map's current boundaries on window
        bounds: {
          north: mapBounds._northEast && mapBounds._northEast.lat,
          south: mapBounds._southWest && mapBounds._southWest.lat,
          west: mapBounds._southWest && mapBounds._southWest.lng,
          east: mapBounds._northEast && mapBounds._northEast.lng
        },
        types: ["address"]
      },
      callback
    );
  },
  200
);

function Route(props) {
  const {
    mainRouteId,
    solution,
    projectDate,
    warnReopen,
    driverDragging,
    projectId,
    colorsObj,
    shareRoute,
    project,
    mapBounds,
    published,
    handleStopAdd,
    setLoadingRoutesList,
    dragFromUnassignedToRoute,
    projectStatus,
    dragSelection,
    setDragSelection,
    setDragSelectionByArr,
    setShowingRouteId,
    setExtendableId,
    setExtendableType,
    setManualDragSelection,
    setProjectTabValue
  } = props;

  // General
  const classes = useStyles();

  // States
  // const [selectedStops, setSelectedStops] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchingForSuggestion, setSearchingForSuggestion] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [changesDialog, setChangesDialog] = useState({ visible: false });
  const [haveHovered, setHaveHovered] = useState(false);
  const listKeyboardCommands = useListKeyboardCommands();

  // Refs
  const googleMapsAutocompleteSessionToken = useRef(
    new window.google.maps.places.AutocompleteSessionToken()
  );

  const googleMapsAutocompleteService = useRef(
    new window.google.maps.places.AutocompleteService()
  );

  // Memos
  const { backhaulGroup, isRouteEmpty, areAllRoutesEmpty } = useMemo(() => {
    let theGroup = [];
    let isRouteEmpty = false;
    let areAllRoutesEmpty = false;
    if (solution && solution.routeGroups) {
      Object.keys(solution.routeGroups).forEach(groupId => {
        theGroup = solution.routeGroups[groupId].find(routeGroup =>
          routeGroup.map(route => route.id).includes(mainRouteId)
        );
      });
    }

    if (
      !theGroup ||
      !theGroup[0]?.stops.some(
        stop => !["start", "end", "break"].includes(stop.type)
      )
    ) {
      isRouteEmpty = true;
      //Check if there is any route.
      areAllRoutesEmpty = !solution.routes.some(route =>
        route.stops.some(stop => !["start", "end", "break"].includes(stop.type))
      );
    }

    return {
      backhaulGroup: theGroup || [],
      isRouteEmpty,
      areAllRoutesEmpty
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainRouteId, solution, project]);

  const filteredBackhaulGroup = backhaulGroup.filter(route =>
    route.stops.some(stop => !["start", "end", "break"].includes(stop.type))
  );

  // Variables
  const unassigned = solution ? solution.unassigned.stops : [];
  const hasBackhaul = filteredBackhaulGroup.length > 1;
  const stopsByRoute = [];

  backhaulGroup.forEach(route => {
    route.stops.forEach(stop => {
      stopsByRoute[stop.id] = route.id;
    });
  });
  const allStops = backhaulGroup.map(routeGroup => routeGroup.stops).flat();
  const allNonStartEndStops = allStops.filter(
    stop => stop.type !== "start" && stop.type !== "end"
  );
  const allNonStartEndBreakStops = allStops.filter(
    stop =>
      stop.type !== "start" && stop.type !== "end" && stop.type !== "break"
  );
  const mainRoute = backhaulGroup[0];
  const dragSelectionList = dragSelection?.list ? dragSelection.list : [];
  const selectedStopsRouteIds = dragSelectionList
    .map(stopId => allStops.find(stop => stop.id === stopId) || [])
    .reduce(
      (currentRouteIds, stop) =>
        !currentRouteIds.includes(stopsByRoute[stop.id])
          ? [...currentRouteIds, stopsByRoute[stop.id]]
          : currentRouteIds,
      []
    );

  // Customs
  const [filteredStops, stopsInfoData] = useStopsFilter(searchInputValue, {
    unscheduled: [],
    scheduled: allNonStartEndBreakStops
  });

  // Callbacks
  const getColor = id => (colorsObj[id] >= 0 ? colorsObj[id] : 0);
  const handleSelectAllClick = () => {
    if (dragSelectionList.length !== filteredStops.scheduled.length) {
      const newSelected = filteredStops.scheduled.map(stop => stop.id);
      setDragSelectionByArr(`route-${mainRoute?.id}`, newSelected);
      return;
    }
    setDragSelectionByArr("", []);
  };

  const toggleStopSelect = stopId => {
    setDragSelection(`route-${mainRoute?.id}`, stopId);
  };

  const suggestionResultsCallback = useCallback(
    results => {
      const theSuggestions = results
        ? results.map(suggestion => ({
            el: suggestion,
            info: [
              <FormattedText
                query={searchInputValue}
                text={suggestion.description?.split(",")[0]}
              />,
              <FormattedText
                query={searchInputValue}
                text={suggestion.description?.split(",").slice(1).join(",")}
              />
            ]
          }))
        : [];
      setSuggestions(theSuggestions);
      setSearchingForSuggestion(false);
    },
    [searchInputValue]
  );

  const focusSearchField = () => {
    const el = document.getElementById("project-searchInput");
    el && el.focus();
  };

  const handleSuggestionAdd = selectedItem => {
    setLoadingRoutesList(true);
    if (selectedItem && selectedItem.place_id) {
      // Get place details
      const service = new window.google.maps.places.PlacesService(
        window.document.createElement("div")
      );
      service.getDetails(
        {
          placeId: selectedItem.place_id,
          language: "en",
          fields: ["address_components", "geometry", "formatted_address"]
        },
        function (PlaceResult, PlacesServiceStatus) {
          const body = { address: PlaceResult, id: null };
          // Post service changes
          handleStopAdd(body, {
            track: true,
            callback: (error, response) => {
              if (!error) {
                let destinationRoute = backhaulGroup[backhaulGroup.length - 1];
                dragFromUnassignedToRoute({
                  destinationId: destinationRoute.id,
                  destIndex: destinationRoute.stops.length - 1,
                  selection: [response.id],
                  successMessage: "Stop added.",
                  callback: () => {
                    focusSearchField();
                    setSuggestions([]);
                    setSearchInputValue("");
                    setLoadingRoutesList(false);
                  }
                });
              } else {
                setLoadingRoutesList(false);
              }
            }
          });
        }
      );
    }
  };

  const editStop = useCallback(
    (id, status) => {
      setExtendableId(id);
      setExtendableType("stops", { status });
    },
    [setExtendableId, setExtendableType]
  );

  const warnEdit = useCallback(
    (e, id, type, isEditing, status) => {
      e.stopPropagation();
      if (!isEditing) {
        if (!store.getState().extendable.saveDisabled) {
          setChangesDialog({ visible: true, id, status });
        } else {
          if (type === "break") {
            setManualDragSelection(`route-${mainRoute?.id}`, [id]);
            if (!published) {
              setExtendableId(mainRoute?.vehicle_id);
              setExtendableType("vehicles", { isBreak: true });
            }
          } else {
            editStop(id, status);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mainRoute?.id, published, editStop, mainRoute?.vehicle_id]
  );

  // Effects
  useEffect(() => {
    if (searchInputValue !== "") {
      if (!published && !mainRoute?.locked) {
        setSearchingForSuggestion(true);
        getSuggestionDebounce(
          googleMapsAutocompleteService.current,
          googleMapsAutocompleteSessionToken.current,
          mapBounds,
          searchInputValue,
          suggestionResultsCallback
        );
      }
    } else {
      suggestionResultsCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputValue, suggestionResultsCallback, published]);

  useEffect(() => {
    return () => {
      setDragSelectionByArr("", []);
    };
  }, []);

  useEffect(() => {
    listKeyboardCommands.methods.setListSize(
      filteredStops.scheduled.length + suggestions.length
    );
  }, [filteredStops, suggestions, listKeyboardCommands.methods]);

  useEffect(() => {
    if (searchInputValue === "") {
      listKeyboardCommands.methods.stop();
    } else {
      listKeyboardCommands.methods.reset();
      listKeyboardCommands.methods.start();
    }
  }, [listKeyboardCommands.methods, searchInputValue]);

  listKeyboardCommands.methods.setEnterHandler((event, index) => {
    if (index < filteredStops.scheduled.length) {
      const stop = filteredStops.scheduled[index];
      warnEdit(event, stop.id, stop.type, false, stop.status);
    } else {
      handleSuggestionAdd(
        suggestions[index - filteredStops.scheduled.length].el
      );
    }
  });

  if (areAllRoutesEmpty) {
    setShowingRouteId("");
    setProjectTabValue(0);
    return <></>;
  }

  if (isRouteEmpty) {
    setShowingRouteId("");
    return <></>;
  }

  if (!projectDate) {
    return "";
  }

  return (
    <>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{
          height: "calc(100vh - 80px)",
          maxHeight: "calc(100vh - 80px)",
          overflow: "hidden"
        }}
      >
        <Grid item>
          <RouteHeader
            backhaulGroup={backhaulGroup}
            selectedStops={dragSelectionList}
            handleSelectAllClick={handleSelectAllClick}
            searchInputValue={searchInputValue}
            setSearchInputValue={setSearchInputValue}
            onSearchFocus={() => {
              listKeyboardCommands.methods.enable();
            }}
            onSearchBlur={() => {
              listKeyboardCommands.methods.disable();
            }}
          />
        </Grid>
        <Grid
          item
          xs
          style={{ maxHeight: "calc(100vh - 204px)", overflow: "auto" }}
        >
          {searchInputValue !== "" && (
            <div>
              {searchingForSuggestion && (
                <SearchBoxMessage
                  message="Searching for stops..."
                  className={classes.searchBoxMessage}
                />
              )}
              {!searchingForSuggestion &&
                suggestions.length === 0 &&
                filteredStops.scheduled.length === 0 && (
                  <SearchBoxMessage
                    message={`No stops found for "${searchInputValue}"`}
                    className={classes.searchBoxMessage}
                  />
                )}
              {!searchingForSuggestion &&
                (suggestions.length > 0 ||
                  filteredStops.scheduled.length > 0) && (
                  <Grid container direction="column">
                    {filteredStops.scheduled.length > 0 && (
                      <>
                        <Grid item>
                          <Typography className={classes.title}>
                            Stops in route
                          </Typography>
                        </Grid>
                        {filteredStops.scheduled.map((filteredStop, index) => (
                          <Grid item container>
                            <DragItem
                              item={filteredStop}
                              index={index}
                              stopInfo={stopsInfoData.scheduled[index]}
                              container
                              color={
                                routeColors[
                                  getColor(mainRouteId) % routeColors.length
                                ]
                              }
                              count={
                                allNonStartEndBreakStops.findIndex(
                                  stop => filteredStop.id === stop.id
                                ) + 1
                              }
                              projectStatus={projectStatus}
                              selected={dragSelectionList.includes(
                                filteredStop.id
                              )}
                              hasSelected={dragSelectionList.length > 0}
                              handleSelect={toggleStopSelect}
                              editOnClick
                              published
                              editStop={warnEdit}
                              activeByListCommand={
                                index === listKeyboardCommands.currentIndex
                              }
                              setHaveHovered={setHaveHovered}
                              haveHovered={haveHovered}
                            />
                          </Grid>
                        ))}
                      </>
                    )}
                    {suggestions.length > 0 && (
                      <>
                        <Grid item className={classes.suggestionTitle}>
                          <Typography className={classes.title}>
                            Add new stop
                          </Typography>
                        </Grid>
                        {suggestions.map((suggestion, index) => (
                          <Grid item container direction="row">
                            <Stop
                              handleItemSelect={handleSuggestionAdd}
                              key={suggestion.el.id}
                              el={suggestion.el}
                              stopInfo={suggestion.info}
                              isSuggestion
                              suggestionIndex={index}
                              setHaveHovered={setHaveHovered}
                              haveHovered={haveHovered}
                              isActive={
                                filteredStops.scheduled.length + index ===
                                listKeyboardCommands.currentIndex
                              }
                            />
                          </Grid>
                        ))}
                      </>
                    )}
                  </Grid>
                )}
            </div>
          )}
          {searchInputValue === "" && (
            <>
              <Typography className={classes.title}>Stops in route</Typography>
              <UnscheduledBar unassigned={unassigned} warnReopen={warnReopen} />
              {filteredBackhaulGroup.map((route, routeIndex) => (
                <Droppable
                  droppableId={`panel-${route.id}`}
                  isDropDisabled={!driverDragging}
                >
                  {(provided, snapshot) => {
                    return (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        <DragList
                          route={route}
                          projectId={projectId}
                          color={
                            routeColors[
                              getColor(mainRouteId) % routeColors.length
                            ]
                          }
                          hideStart={hasBackhaul && routeIndex > 0}
                          showReload={
                            hasBackhaul &&
                            routeIndex < filteredBackhaulGroup.length - 1
                          }
                          nextStart={
                            hasBackhaul &&
                            routeIndex < filteredBackhaulGroup.length - 1
                              ? filteredBackhaulGroup[routeIndex + 1].stops[0]
                              : undefined
                          }
                          countStart={
                            routeIndex > 0
                              ? allNonStartEndBreakStops.findIndex(
                                  stop =>
                                    stop.id ===
                                    route.stops.find(
                                      stop =>
                                        stop.type !== "start" &&
                                        stop.type !== "end" &&
                                        stop.type !== "break"
                                    ).id
                                )
                              : 0
                          }
                          toggleStopSelect={toggleStopSelect}
                          selectedStops={dragSelectionList}
                          disableDrag={
                            selectedStopsRouteIds.length > 1 ||
                            allStops.filter(
                              stop =>
                                dragSelectionList.includes(stop.id) &&
                                (stop.status === "finished" ||
                                  stop.status === "skipped")
                            ).length > 0
                          }
                        />
                      </div>
                    );
                  }}
                </Droppable>
              ))}
            </>
          )}
        </Grid>
        {dragSelectionList.length === 0 && searchInputValue === "" && (
          <Grid item style={{ height: 64 }}>
            <RouteBottomActions
              backhaulGroup={backhaulGroup}
              shareRoute={shareRoute}
            />
          </Grid>
        )}
        <UnsavedChangesDialog
          open={changesDialog.visible}
          handleClose={() => setChangesDialog({ visible: false })}
          action={() => {
            if (changesDialog.isDragSelection) {
              setExtendableType(null);
              setExtendableId(null);
            } else {
              editStop(changesDialog.id, changesDialog.status);
            }
          }}
        />
      </Grid>
    </>
  );
}

const mapStateToProps = state => ({
  projectDate: state.project.project_date,
  mainRouteId: state.project.showingRouteId,
  solution: state.project.solution,
  project: state.project,
  driverDragging: state.common.draggingType === "drivers",
  projectId: state.project.id,
  colorsObj: state.project.solution && state.project.solution.routeColors,
  mapBounds: state.map.bounds,
  published: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  projectStatus: state.project.status,
  dragSelection:
    state.common.dragSelection.listId ===
    `route-${state.project.showingRouteId}`
      ? state.common.dragSelection
      : null
});

export default connect(mapStateToProps, {
  setShowingRouteId,
  handleStopAdd,
  setLoadingRoutesList,
  dragFromUnassignedToRoute,
  setDragSelection,
  setDragSelectionByArr,
  setExtendableId,
  setExtendableType,
  setManualDragSelection,
  setProjectTabValue
})(Route);
